import {AbGlyph} from "../brand/AbMark";
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {store} from '../../store/Store';
import {setPlayerQueue, setPlayerSong} from '../../store/actions/Actions';
import {logout as sanctumLogout} from '../../libraries/Authentication';
import {getLikedTracks, isAutoplayEnabled, likeOnApple} from '../../libraries/MusicKit';
import usePlayerEngine from '../../hooks/usePlayerEngine';
import useSimilars from '../../hooks/useSimilars';
import AmbientBackdrop from '../players/AmbientBackdrop';
import ListeningRoom from '../players/ListeningRoom';
import ArtistView from '../players/ArtistView';
import AlbumView from '../players/AlbumView';
import LikedDrawer from '../players/LikedDrawer';
import MiniDock from '../players/MiniDock';
import SearchBar from '../search/SearchBar';
import SearchResultsView from '../search/SearchResultsView';

// The visible rows/queue cap (the cascade walks the same list the skip does).
const QUEUE_SIZE = 50;

/**
 * The engine showcase: search is the front door, one song in and the engine
 * takes it from there. Screens (home, room, results, artist, album) are real
 * browser history entries, so Back walks the app instead of leaving it.
 */
const Home = ({user}) => {
    const {currentUser, isLoaded} = user || {};
    const dispatch = useDispatch();
    const engine = usePlayerEngine();
    const {playingSong, coverUrl} = engine;

    const [showLiked, setShowLiked] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    // {type: home|room|search|artist|album, ...}; mirrored into history.state.
    const [screen, setScreen] = useState(() => window.history.state?.ab?.screen || {type: 'home'});
    // The track Up Next is anchored to; picking a song re-anchors.
    const [seedSong, setSeedSong] = useState(null);
    // The Apple likes playlist: undefined = loading, null = Apple not connected.
    const [liked, setLiked] = useState(undefined);
    const [toast, setToast] = useState(null);
    const searchRef = useRef(null);
    const toastTimer = useRef(null);

    // Seed history so the first Back inside the app lands on home, and keep
    // screen in sync when the browser's own Back/Forward buttons are used.
    useEffect(() => {
        if (!window.history.state?.ab) {
            window.history.replaceState({ab: {screen: {type: 'home'}, depth: 0}}, '');
        }
        const onPop = (e) => setScreen(e.state?.ab?.screen || {type: 'home'});
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    const navigate = (next) => {
        const depth = (window.history.state?.ab?.depth || 0) + 1;
        window.history.pushState({ab: {screen: next, depth}}, '');
        setScreen(next);
    };
    const goBack = () => {
        if ((window.history.state?.ab?.depth || 0) > 0) {
            window.history.back();
        } else {
            setScreen({type: 'home'});
        }
    };

    const refreshLiked = () => getLikedTracks()
        .then(setLiked)
        .catch(() => setLiked((prev) => prev || {playlistId: null, tracks: []}));
    useEffect(() => {
        refreshLiked();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engine.isAppleAuthorized]);
    const likedIds = useMemo(
        () => new Set((liked?.tracks || []).map((t) => String(t.apple_id))),
        [liked],
    );

    // ⌘K / Ctrl+K focuses search from anywhere.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // The recommendation queue for the anchored song (lifted from the old
    // Up Next panel; the cascade and the skip button both walk this list).
    // Engine locks, per listener (the same knobs the API exposes).
    const [locks, setLocks] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('abtunes_locks') || '{}');
        } catch (e) {
            return {};
        }
    });
    const toggleLock = (k) => {
        const next = {...locks, [k]: !locks[k]};
        setLocks(next);
        try { localStorage.setItem('abtunes_locks', JSON.stringify(next)); } catch (e) { /* noop */ }
    };
    const lockParams = [locks.genre && 'genre_lock=1', locks.era && 'era_lock=1'].filter(Boolean).join('&');
    const {similars} = useSimilars(seedSong, {sort: true, params: lockParams});
    const queueList = useMemo(() => (Array.isArray(similars) ? similars.slice(0, QUEUE_SIZE) : []), [similars]);
    useEffect(() => {
        if (queueList.length && queueList !== engine.playerQueue) {
            dispatch(setPlayerQueue(queueList));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queueList]);

    useEffect(() => {
        if (!seedSong && playingSong.id) {
            setSeedSong(playingSong);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playingSong.id, seedSong]);

    const logoutHandler = async (e) => {
        e.preventDefault();
        const token = store.getState().auth?.jwtToken;
        await sanctumLogout(token);
        window.location = '/login';
    };

    // Safari autoplay unlock (silent clip on the user gesture) before playing.
    const startSong = (song, list) => {
        const go = () => dispatch(setPlayerSong(song, list));
        if (!isAutoplayEnabled()) {
            const empty = new Audio('audio/empty.mp3');
            empty.play().then(() => {
                empty.pause();
                empty.currentTime = 0;
                go();
            }).catch(go);
        } else {
            go();
        }
    };

    const goHome = () => screen.type !== 'home' && navigate({type: 'home'});
    const openArtist = (name) => navigate({type: 'artist', name});
    const openAlbum = (id) => navigate({type: 'album', id});
    const openResults = (q) => navigate({type: 'search', q});
    const openRoom = () => screen.type !== 'room' && navigate({type: 'room'});

    // A picked song anchors the journey: the engine builds everything after it.
    const playAnchored = (song) => {
        setSeedSong(song);
        startSong({...song, parentId: song.id, _intentional: true}, [song]);
        openRoom();
    };
    // Cascade rows: play within the current journey (anchor stays).
    const playFromQueue = (song, list) => {
        startSong({...song, parentId: seedSong ? seedSong.id : song.parentId, _intentional: true}, list);
    };

    const flash = (msg) => {
        clearTimeout(toastTimer.current);
        setToast(msg);
        toastTimer.current = setTimeout(() => setToast(null), 3200);
    };
    // Like = into the user's Apple Music playlist "Discovered by AbTunes.com".
    const like = async (song) => {
        const appleId = song.apple_id;
        if (!appleId || likedIds.has(String(appleId))) {
            flash(appleId ? 'Already in Discovered by AbTunes.com' : "This one isn't on Apple Music");
            return;
        }
        try {
            const ok = await likeOnApple(appleId);
            if (!ok) {
                throw new Error('like failed');
            }
            setLiked((prev) => ({
                playlistId: prev?.playlistId || null,
                tracks: [...(prev?.tracks || []), {
                    apple_id: String(appleId), title: song.songTitle || song.title,
                    artist: song.artist, album: song.album, cover: song.cover,
                }],
            }));
            // Apple's library takes a moment; re-read for the real playlist id.
            setTimeout(refreshLiked, 2500);
            flash('Added to Discovered by AbTunes.com in Apple Music');
        } catch (e) {
            flash('Connect Apple Music to build your playlist');
        }
    };

    const gated = !isLoaded || !currentUser;
    const initial = (currentUser?.attributes?.email || '?').charAt(0).toUpperCase();
    const inRoom = playingSong.id && screen.type === 'room';
    const onHome = !inRoom && !['search', 'artist', 'album'].includes(screen.type);

    return (
        <>
            {gated ? (
                <div className="flex min-h-screen items-center justify-center bg-ambient-base text-white/70">
                    Loading…
                </div>
            ) : (
                <div className="relative h-screen overflow-hidden bg-ambient-base font-display text-white">
                    <AmbientBackdrop coverUrl={coverUrl}/>
                    {/* The room: warm layered glow, breathing at the song's BPM. */}
                    <div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            backgroundImage:
                                'radial-gradient(52% 60% at 68% 8%, rgba(250,45,72,.20), transparent 65%),' +
                                'radial-gradient(45% 55% at 12% 85%, rgba(255,158,68,.14), transparent 62%),' +
                                'radial-gradient(40% 50% at 90% 80%, rgba(122,40,80,.18), transparent 65%)',
                        }}
                    />
                    <div
                        className="pointer-events-none absolute -inset-[10%] animate-breathe"
                        style={{
                            backgroundImage: 'radial-gradient(30% 35% at 50% 40%, rgba(250,45,72,.09), transparent 70%)',
                            animationDuration: (playingSong.id && playingSong.bpm ? (120 / playingSong.bpm).toFixed(2) : 8) + 's',
                        }}
                    />

                    <div className="relative z-10 flex h-full flex-col">
                        {/* Top bar: logo · history · account. */}
                        <header className="flex flex-none items-center gap-3 px-4 py-3 sm:px-6">
                            <button type="button" onClick={goHome} title="Home" className="flex flex-none items-center gap-2.5">
                                <AbGlyph size={24}/>
                                <span className="hidden text-[15px] font-bold tracking-tight sm:inline">AbTunes</span>
                            </button>

                            <div className="flex min-w-0 flex-1 justify-center px-2">
                                {!onHome && <SearchBar ref={searchRef} onPlay={playAnchored} onResults={openResults}/>}
                            </div>

                            <div className="flex flex-none items-center gap-2">
                                <button type="button" onClick={() => setShowLiked(true)} title="Your playlist"
                                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white">
                                    <i className="fa-solid fa-list text-[13px]"/>
                                </button>
                                <div className="relative">
                                    <button type="button" onClick={() => setShowMenu(!showMenu)} title="Account"
                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-apple-red to-[#7a1a2a] text-[13px] font-extrabold">
                                        {initial}
                                    </button>
                                    {showMenu && (
                                        <>
                                            <div className="fixed inset-0 z-30" onClick={() => setShowMenu(false)}/>
                                            <div className="absolute right-0 top-11 z-40 w-56 overflow-hidden rounded-2xl bg-white/[0.08] py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_24px_60px_rgba(0,0,0,0.5)] backdrop-blur-2xl">
                                                <button type="button"
                                                        onClick={() => { setShowMenu(false); (engine.isAppleAuthorized ? engine.onClickLogoutFromApple : engine.onClickLoginToApple)(); }}
                                                        className="block w-full px-4 py-2.5 text-left text-[13px] font-semibold text-white/80 transition hover:bg-white/10 hover:text-white">
                                                    {engine.isAppleAuthorized ? 'Disconnect Apple Music' : 'Connect Apple Music'}
                                                </button>
                                                <button type="button"
                                                        onClick={() => { setShowMenu(false); engine.onClickAppleMusicLogo(); }}
                                                        className="block w-full px-4 py-2.5 text-left text-[13px] font-semibold text-white/80 transition hover:bg-white/10 hover:text-white">
                                                    Open Apple Music
                                                </button>
                                                <div className="my-1 h-px bg-white/10"/>
                                                {[['genre', 'Genre lock', 'Stay in the same genre family'],
                                                    ['era', 'Era lock', 'Within 10 years of the song']].map(([k, label, hint]) => (
                                                    <button key={k} type="button" onClick={() => toggleLock(k)}
                                                            className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-white/10">
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block text-[13px] font-semibold text-white/80">{label}</span>
                                                            <span className="block text-[11px] text-white/40">{hint}</span>
                                                        </span>
                                                        <span className={'relative h-[18px] w-8 flex-none rounded-full transition '
                                                            + (locks[k] ? 'bg-apple-red' : 'bg-white/20')}>
                                                            <span className={'absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all '
                                                                + (locks[k] ? 'left-[16px]' : 'left-[2px]')}/>
                                                        </span>
                                                    </button>
                                                ))}
                                                <div className="my-1 h-px bg-white/10"/>
                                                <button type="button" onClick={logoutHandler}
                                                        className="block w-full px-4 py-2.5 text-left text-[13px] font-semibold text-white/80 transition hover:bg-white/10 hover:text-white">
                                                    Log out
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </header>

                        {/* One open-air surface: the room, a door, or the welcome. */}
                        <main className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 lg:pb-28">
                            <div>
                                {inRoom ? (
                                    <ListeningRoom
                                        engine={engine}
                                        onArtist={openArtist}
                                        onAlbum={openAlbum}
                                        onPlayFromQueue={playFromQueue}
                                    />
                                ) : screen.type === 'search' ? (
                                    <SearchResultsView q={screen.q} onPlay={playAnchored} onAlbum={openAlbum} onBack={goBack}/>
                                ) : screen.type === 'artist' ? (
                                    <ArtistView name={screen.name} onPlay={playAnchored} onAlbum={openAlbum} onBack={goBack}/>
                                ) : screen.type === 'album' ? (
                                    <AlbumView albumId={screen.id} onPlay={playAnchored} onArtist={openArtist} onBack={goBack}/>
                                ) : (
                                    <div className="flex min-h-[70vh] flex-col items-center justify-center text-center">
                                        <h1 className="font-heading text-[34px] font-bold leading-[1.1] tracking-[-0.02em] sm:text-[48px]">
                                            Pick a song.<br/><span className="text-white/50">We'll handle the rest.</span>
                                        </h1>
                                        <p className="mb-8 mt-4 max-w-[440px] text-[14px] text-white/[0.45] sm:text-[15px]">
                                            AbTunes reads the sound of the song you choose and keeps playing
                                            what genuinely sounds close.
                                        </p>
                                        <SearchBar hero ref={searchRef} onPlay={playAnchored} onResults={openResults}/>
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>

                    {playingSong.id && (
                        <MiniDock
                            engine={engine}
                            isLiked={likedIds.has(String(playingSong.apple_id))}
                            onLike={() => like(playingSong)}
                            onExpand={openRoom}
                        />
                    )}
                    {toast && (
                        <div className="fixed bottom-28 left-1/2 z-50 -translate-x-1/2 rounded-full bg-white/[0.10] px-4 py-2 text-[12.5px] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_18px_44px_rgba(0,0,0,0.45)] backdrop-blur-2xl">
                            {toast}
                        </div>
                    )}
                    <LikedDrawer
                        open={showLiked}
                        onClose={() => setShowLiked(false)}
                        liked={liked}
                        onRefresh={refreshLiked}
                        onConnect={engine.onClickLoginToApple}
                        onPlay={playAnchored}
                    />
                </div>
            )}

            <audio ref={engine.audioRef} src={engine.previewSong || undefined} loop={false} autoPlay={false} preload="auto"/>
        </>
    );
};

export default Home;
